import { useCallback } from 'react';
import type { BoundingBoxType, QueryParserResult } from '@/fetchers/getQueryParserResult';
import type { AutocompleteData } from '@/utils/autocompleteHelper';
import { geoQueryParser } from '@/actions/geoCodeQueryParser';
import { decodeGeo, encodeGeoForUrl } from '@/actions/geoCodeEncoder';
import { type LatLng, type LatLngBoundsLiteral, scaleBoundsToZoom } from '@/utils/maps/mapUtils';
import { DEFAULT_ZOOM } from '@/components/maps/GoogleMap';
import { MAP_DIMENSION } from '@/constants/mapConstants';

type GeoLocationData = {
  hasError: boolean;
  geocode?: string;
};
/**
 * Return encoded string from address & place id
 */
export default function useGeocoder() {
  const getGeolocationData = useCallback(
    async (
      term: string,
      autoSuggestion?: AutocompleteData,
      bounds?: LatLngBoundsLiteral,
      calculateBoundForMap?: boolean
    ): Promise<GeoLocationData> => {
      const geoLocationData = (await geoQueryParser(
        autoSuggestion ? null : term,
        autoSuggestion ? (autoSuggestion as AutocompleteData) : null
      )) as QueryParserResult;

      if (!geoLocationData.geographicPointFilter?.geographicPoint || !geoLocationData.boundingBoxFilter?.boundingBox) {
        return { hasError: true };
      }
      if (calculateBoundForMap) {
        geoLocationData.boundingBoxFilter.boundingBox = scaleBoundsToZoom(
          {
            lng: geoLocationData?.geographicPointFilter?.geographicPoint.lon,
            lat: geoLocationData?.geographicPointFilter?.geographicPoint.lat
          },
          DEFAULT_ZOOM,
          MAP_DIMENSION
        );
      }
      if (bounds) {
        geoLocationData.boundingBoxFilter.boundingBox.northEastPoint.lat = bounds.north;
        geoLocationData.boundingBoxFilter.boundingBox.northEastPoint.lon = bounds.east;
        geoLocationData.boundingBoxFilter.boundingBox.southWestPoint.lat = bounds.south;
        geoLocationData.boundingBoxFilter.boundingBox.southWestPoint.lon = bounds.west;
      }
      const geoCode = await encodeGeoForUrl(geoLocationData as QueryParserResult);
      return { hasError: false, geocode: geoCode as string };
    },
    []
  );

  const updateBoundingBox = useCallback(
    async (geo: string, bounds: BoundingBoxType): Promise<{ hasError: false; geocode: string }> => {
      const geoLocationData = await decodeGeo(geo);

      if (geoLocationData && geoLocationData.boundingBoxFilter) {
        geoLocationData.boundingBoxFilter.boundingBox = bounds;
      }

      const geoCode = await encodeGeoForUrl(geoLocationData as QueryParserResult);
      return { hasError: false, geocode: geoCode as string };
    },
    []
  );

  const getGeoDataByMap = useCallback(
    async (geoLocationData: QueryParserResult, bounds: LatLngBoundsLiteral, latlng: LatLng): Promise<string> => {
      const newQueryParserResult = { ...geoLocationData };
      if (
        newQueryParserResult.boundingBoxFilter &&
        newQueryParserResult.boundingBoxFilter.boundingBox &&
        newQueryParserResult.geographicPointFilter
      ) {
        newQueryParserResult.boundingBoxFilter.boundingBox.northEastPoint.lat = bounds.north;
        newQueryParserResult.boundingBoxFilter.boundingBox.northEastPoint.lon = bounds.east;
        newQueryParserResult.boundingBoxFilter.boundingBox.southWestPoint.lat = bounds.south;
        newQueryParserResult.boundingBoxFilter.boundingBox.southWestPoint.lon = bounds.west;
        newQueryParserResult.geographicPointFilter.geographicPoint.lat = latlng.lat;
        newQueryParserResult.geographicPointFilter.geographicPoint.lon = latlng.lng;
        // clear polygon filter
        if (geoLocationData.additionalFilters) {
          newQueryParserResult.additionalFilters = geoLocationData.additionalFilters.filter(
            filter => filter.type !== 'POLYGON'
          );
        }
      }
      return (await encodeGeoForUrl(newQueryParserResult)) as string;
    },
    []
  );

  return { getGeolocationData, updateBoundingBox, getGeoDataByMap };
}
